import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { retry, catchError, tap } from 'rxjs/operators';
import jwt_decode from "jwt-decode";
import { environment } from '../../../environments/environment';
import { SharedService } from '../services/shared.service';
import { TokenStorageService }  from "./token-storage.service";
import { IAuthTokenResponse } from '../../common/interfaces/auth-token-response';
import { IAuthToken } from '../interfaces/auth-token';
import { IBrandProfile } from '../interfaces/brand-profile';

const SCOPE = 'mobills-portal'; // ei-blond to make the token expire in one minute
const GRANT_TYPE = 'password';
const USER_NAME = 'user-name';

@Injectable({
  providedIn: 'root'
})
export class UserAuthService {

  private userPrivilegesUrl = "/auth/users/{userName}/privileges";  
  private passwordResetRequestUrl = '/auth/users/password-reset';
  private passwordResetUrl = "/auth/users/{userName}/reset-password";

  constructor(
    private http: HttpClient,
    private tokenStorageService: TokenStorageService,
    private sharedService: SharedService    
  ) { }

  // Save the user name in the local storage
  public setUserName (userName: string): void {
    localStorage.removeItem(USER_NAME);
    localStorage.setItem(USER_NAME, userName);
  }

  // Retrieve the user name from the local storage
  public getUserName(): string | null {
    return localStorage.getItem(USER_NAME);
  }   

  public logout() {
    // clean up current brand and its profile
    // let emptyBrandProfile: IBrandProfile = {
    //   brandCode: '',
    //   brandName: ''
    // };
    // this.sharedService.setEnvRegion('');
    // this.sharedService.setBaseUrl('');
    // this.sharedService.setBrandCode('');
    // this.sharedService.setBrandProfile(emptyBrandProfile);
    // this.sharedService.setAccountNum('');
    // this.tokenStorageService.setAuthToken('')
    // this.tokenStorageService.setUserPrivileges(['']);   
    this.sharedService.clearUserSession(); 
  }

  // Request an email with a password reset instruction
  public requestPasswordReset(userName: string, emailAddress: string): Observable<string[]> {
    let appName = "walletron-agent";
    let passwordResetUrl = environment.passwordResetUrl;
    let pswResetRequestUrl = this.getAuthServerUrl() + this.passwordResetRequestUrl;  
    let body = `appName=${appName}&userName=${userName}&emailAddress=${emailAddress}&passwordResetUrl=${passwordResetUrl}`;
    let headers = this.getAuthHeaders();
    return this.http.post<string[]>(pswResetRequestUrl, body, {headers: headers, responseType: 'json'})
    .pipe(
      catchError(this.sharedService.handleError2<string[]>('login'))
    )
  }

  // Submit a request with a new password
  public postPasswordReset(userName: string, newPassword: string, signedJwt: string): Observable<string> {
    let url = this.getAuthServerUrl() + this.passwordResetUrl;
    url = url.replace('{userName}', userName);
    let request = {
      newPassword: newPassword,
      signedJwt: signedJwt
    }
    return this.http.put<string>(url, request, { responseType: 'json'})
  }   

  // Prepare, then make a request to obtain an auth token
  public getAuthToken(userName: string, password: string): Observable<IAuthTokenResponse> {
    let tokenRquestUrl = this.getAuthServerUrl() + '/token';  
    let body = `grant_type=${GRANT_TYPE}&username=${userName}&password=${password}&scope=${SCOPE}`;
    let headers = this.getAuthHeaders();
    return this.http.post<IAuthTokenResponse>(tokenRquestUrl, body, {headers: headers, responseType: 'json'})
    .pipe(
      catchError(this.sharedService.handleError2<IAuthTokenResponse>('login' ))
    )
  }  

  // Find out the user's profile, including roles, brands, and attributes
  findUserPrivileges (userName: string): Observable<string[]> {
    let url = this.sharedService.getAuthServerBaseUrl() 
      + this.userPrivilegesUrl.replace('{userName}', userName);
    return this.http.get<string[]>(url, {responseType: 'json'})
    .pipe(
      catchError(this.sharedService.handleError)
    )
  }

  // Verify whether the auth token has not expired yet
  public isTokenValid(): Observable<boolean> {
    let isTokenExpired: boolean = false;
    let tokenString: string = this.tokenStorageService.getAuthToken() || '';
    if (tokenString) {
      let authToken : IAuthToken = jwt_decode(tokenString);
      const now = new Date();
      var utcEpoch = now.getTime();
      console.log('utcEpoch', utcEpoch);
      console.log('authToken.exp', authToken.exp * 1000);
      isTokenExpired = authToken.exp * 1000 > utcEpoch;
    }
    return of(isTokenExpired);
  }

  // Verify that the auth token has required privileges
  public arePrivilegesGranted(requiredPrivileges: string[]): Observable<boolean> {
    let arePrivilegesGranted: boolean = true;
    // Locate cached out user privileges
    let userPrivileges = this.tokenStorageService.getUserPrivileges() || [];
    if (!userPrivileges) {
      arePrivilegesGranted = false;
    }
    // Check out every required privilege making sure it is in the list of user privileges
    requiredPrivileges.forEach(reqPriv => {
        if (userPrivileges.findIndex(item => item == reqPriv) < 0) {
          arePrivilegesGranted = false;
        }
      })
      return of(arePrivilegesGranted);
  }  

  private getAuthServerUrl() {
    return environment.authServerBasePath;
  }

  private getAuthHeaders() {
    let basicAuth = environment.portalBasicAuth;
    let headers = new HttpHeaders()
      .set('Authorization', basicAuth)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return headers;
  }

}
